import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  FiArrowRight,
  FiPackage,
  FiSearch,
  FiTag,
  FiLayers,
  FiX,
  FiChevronRight,
  FiRefreshCw,
} from "react-icons/fi";
import axios from "../../Utils/axiosInstance";
import { searchProductVarieties } from "../../Utils/ProductSearchUtils";

const AllSpecialVarieties = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentSpecialType = searchParams.get("type") || "all";

  const [varieties, setVarieties] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState("all");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const fetchSpecialVarieties = useCallback(async () => {
    try {
      setLoading(true);
      setError(false);

      // 1. Fetch special categories
      const params = { special: true };
      if (currentSpecialType !== "all") {
        params.specialType = currentSpecialType;
      }

      const { data: categoryRes } = await axios.get("/category/get-all", {
        params,
      });

      const specialCategories = categoryRes?.data || [];
      setCategories(specialCategories);
      setSelectedCategoryId("all");

      if (specialCategories.length === 0) {
        setVarieties([]);
        return;
      }

      // 2. Fetch active product varieties for each special category
      const varietyRequests = specialCategories.map((category) =>
        axios.get("/product-variety/get-all", {
          params: {
            categoryId: category._id,
            isActive: true,
            populate: "true",
          },
        })
      );

      const varietyResponses = await Promise.all(varietyRequests);

      // 3. Flatten and tag each variety with its parent category
      const allVarieties = varietyResponses.flatMap((res, index) => {
        const list = res?.data?.data || [];
        return list.map((variety) => ({
          ...variety,
          parentCategory: specialCategories[index],
        }));
      });

      // Deduplicate by variety _id if any overlaps occur
      const uniqueMap = new Map();
      allVarieties.forEach((v) => {
        if (v._id && !uniqueMap.has(v._id)) {
          uniqueMap.set(v._id, v);
        }
      });

      setVarieties(Array.from(uniqueMap.values()));
    } catch (err) {
      console.error("Failed to load special category varieties:", err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [currentSpecialType]);

  useEffect(() => {
    fetchSpecialVarieties();
  }, [fetchSpecialVarieties]);

  // Filter varieties by selected category
  const categoryFilteredVarieties = useMemo(() => {
    if (selectedCategoryId === "all") {
      return varieties;
    }
    return varieties.filter((variety) => {
      const parentCatId =
        variety.parentCategory?._id ||
        variety.productId?.subCategoryId?.categoryId?._id;
      return parentCatId === selectedCategoryId;
    });
  }, [varieties, selectedCategoryId]);

  // Apply search filtering on top of category filtering
  const displayVarieties = useMemo(() => {
    if (!query.trim()) {
      return categoryFilteredVarieties;
    }
    return searchProductVarieties(query.trim(), categoryFilteredVarieties);
  }, [query, categoryFilteredVarieties]);

  // Build target link to /products with full selection parameters
  const getVarietyHref = (variety) => {
    const params = new URLSearchParams();

    const categoryId =
      variety?.productId?.subCategoryId?.categoryId?._id ||
      variety?.productId?.subCategoryId?.categoryId ||
      variety?.parentCategory?._id ||
      "";

    const subCategoryId =
      variety?.productId?.subCategoryId?._id || variety?.subCategoryId || "";

    const productId = variety?.productId?._id || variety?.productId || "";

    if (categoryId) params.set("category", categoryId);
    if (subCategoryId) params.set("subCategory", subCategoryId);
    if (productId) params.set("product", productId);
    if (variety?._id) params.set("variety", variety._id);

    return `/products${params.toString() ? `?${params.toString()}` : ""}`;
  };

  const getPageHeaderInfo = () => {
    if (currentSpecialType === "swadeshi") {
      return {
        badge: "🎨 Swadeshi Special Category",
        title: "SWADESHI (HANDICRAFTS & HANDLOOMS)",
        desc: "Explore authentic handcrafted and handloom treasures crafted with traditional heritage and artisanal skill.",
      };
    }
    if (currentSpecialType === "plant_extracts") {
      return {
        badge: "🌿 Special Category",
        title: "Plant Extracts",
        desc: "Explore our curated range of high-quality plant extracts product varieties.",
      };
    }
    return {
      badge: "✨ Special Categories",
      title: "All Special Category Varieties",
      desc: "Explore our curated collections of Plant Extracts and Swadeshi (Handicrafts & Handlooms).",
    };
  };

  const pageInfo = getPageHeaderInfo();

  return (
    <div className="min-h-screen bg-[#FFFCFC]">
      <div className="2xl:max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        {/* Breadcrumb Navigation */}
        <nav className="mb-6 flex items-center gap-2 text-xs font-medium text-gray-500">
          <Link to="/" className="hover:text-primaryColor transition-colors">
            Home
          </Link>
          <FiChevronRight className="text-gray-400" />
          <span className="text-primaryColor font-semibold">
            {pageInfo.title}
          </span>
        </nav>

        {/* Page Header */}
        <div className="mb-8 md:mb-10">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div>
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primaryColor/10 text-primaryColor text-xs font-semibold mb-2">
                {pageInfo.badge}
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-semibold bg-clip-text text-transparent bg-gradient-to-r from-[#3D0F1B] to-subPrimaryColor py-1">
                {pageInfo.title}
              </h1>
              <p className="mt-2 max-w-2xl text-xs sm:text-sm text-gray-500 leading-relaxed">
                {pageInfo.desc}
              </p>
            </div>

            {!loading && !error && (
              <div className="inline-flex items-center gap-2 self-start sm:self-auto rounded-full bg-primaryColor/10 px-4 py-1.5 text-xs font-semibold text-primaryColor">
                <span>Total:</span>
                <span className="font-bold">{displayVarieties.length}</span>
                <span>varieties</span>
              </div>
            )}
          </div>
        </div>

        {/* Category Type Switcher Tabs */}
        <div className="flex items-center gap-2 overflow-x-auto pb-2 mb-6 scrollbar-none">
          <button
            onClick={() => setSearchParams({})}
            className={`px-4 py-2 rounded-full text-xs font-semibold whitespace-nowrap transition ${
              currentSpecialType === "all"
                ? "bg-primaryColor text-white shadow-sm"
                : "bg-white border border-gray-200 text-gray-700 hover:bg-gray-50"
            }`}
          >
            All Special Varieties
          </button>
          <button
            onClick={() => setSearchParams({ type: "plant_extracts" })}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold whitespace-nowrap transition ${
              currentSpecialType === "plant_extracts"
                ? "bg-primaryColor text-white shadow-sm"
                : "bg-white border border-gray-200 text-gray-700 hover:bg-gray-50"
            }`}
          >
            <span>🌿</span> Plant Extracts
          </button>
          <button
            onClick={() => setSearchParams({ type: "swadeshi" })}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold whitespace-nowrap transition ${
              currentSpecialType === "swadeshi"
                ? "bg-primaryColor text-white shadow-sm"
                : "bg-white border border-gray-200 text-gray-700 hover:bg-gray-50"
            }`}
          >
            <span>🎨</span> SWADESHI (Handicrafts & Handlooms)
          </button>
        </div>

        {/* Sub-Category Pills */}
        {categories.length > 1 && (
          <div className="flex items-center gap-2 overflow-x-auto pb-2 mb-6 scrollbar-none">
            <button
              onClick={() => setSelectedCategoryId("all")}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition ${
                selectedCategoryId === "all"
                  ? "bg-[#3D0F1B] text-white"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              All Categories ({varieties.length})
            </button>
            {categories.map((cat) => (
              <button
                key={cat._id}
                onClick={() => setSelectedCategoryId(cat._id)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition ${
                  selectedCategoryId === cat._id
                    ? "bg-[#3D0F1B] text-white"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>
        )}

        {/* Search Input Section */}
        <div className="mb-8 space-y-4">
          <div className="relative max-w-3xl">
            <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search varieties by name, grade, or product..."
              className="w-full rounded-full border border-gray-200 bg-white py-3.5 pl-12 pr-10 text-sm text-gray-800 shadow-sm outline-none transition focus:border-primaryColor/70 focus:ring-2 focus:ring-primaryColor/10"
            />
            {query && (
              <button
                onClick={() => setQuery("")}
                className="absolute right-3.5 top-1/2 -translate-y-1/2 rounded-full p-1 text-gray-400 hover:text-gray-600 transition"
                aria-label="Clear search"
              >
                <FiX className="text-base" />
              </button>
            )}
          </div>
        </div>

        {/* ERROR STATE */}
        {error && (
          <div className="rounded-3xl border border-dashed border-red-200 bg-red-50/50 p-10 text-center max-w-xl mx-auto">
            <div className="text-4xl mb-3">📡</div>
            <p className="text-base font-semibold text-gray-800">
              Unable to load special varieties right now
            </p>
            <p className="mt-2 text-xs sm:text-sm text-gray-500">
              Please check your network connection and try again.
            </p>
            <button
              onClick={fetchSpecialVarieties}
              className="mt-5 inline-flex items-center gap-2 rounded-full bg-primaryColor px-5 py-2.5 text-xs font-semibold text-white transition hover:bg-primaryBtnHoverColor active:scale-95 shadow-sm"
            >
              <FiRefreshCw className="text-sm" />
              Retry
            </button>
          </div>
        )}

        {/* LOADING SKELETON */}
        {!error && loading && (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {Array.from({ length: 8 }).map((_, index) => (
              <div
                key={index}
                className="rounded-[1.5rem] border border-gray-200 bg-white p-3 shadow-sm animate-pulse"
              >
                <div className="h-44 w-full rounded-2xl bg-gray-100" />
                <div className="mt-4 space-y-2 p-2">
                  <div className="h-4 w-3/4 rounded bg-gray-100" />
                  <div className="h-3 w-1/2 rounded bg-gray-100" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* EMPTY STATE */}
        {!error && !loading && displayVarieties.length === 0 && (
          <div className="rounded-3xl border border-dashed border-gray-200 bg-white p-12 text-center max-w-xl mx-auto shadow-sm">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-primaryColor/10 text-primaryColor mb-4">
              <FiPackage className="h-8 w-8" />
            </div>
            <h3 className="text-lg font-semibold text-gray-800">
              {query
                ? "No special varieties match your search"
                : "No special category product varieties found"}
            </h3>
            <p className="mt-2 text-xs sm:text-sm text-gray-500 max-w-sm mx-auto">
              {query
                ? `We couldn't find any items matching "${query}". Try searching with a different keyword.`
                : "Special category product varieties will appear here once available."}
            </p>
            {query && (
              <button
                onClick={() => setQuery("")}
                className="mt-5 inline-flex items-center gap-2 rounded-full bg-gray-100 px-4 py-2 text-xs font-semibold text-gray-700 transition hover:bg-gray-200"
              >
                <FiX className="text-sm" />
                Clear search
              </button>
            )}
          </div>
        )}

        {/* VARIETIES GRID */}
        {!error && !loading && displayVarieties.length > 0 && (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {displayVarieties.map((variety) => {
              const categoryName =
                variety?.parentCategory?.name ||
                variety?.productId?.subCategoryId?.categoryId?.name ||
                "Special Category";

              const productName =
                variety?.productId?.name || "Product";

              const href = getVarietyHref(variety);

              const isSwadeshi =
                variety?.parentCategory?.specialCategoryType === "swadeshi";

              return (
                <Link
                  to={href}
                  key={variety._id}
                  className="group relative flex flex-col justify-between overflow-hidden rounded-[1.5rem] border border-gray-100 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-primaryColor/30 hover:shadow-xl"
                >
                  <div>
                    {/* Image Area */}
                    <div className="relative h-48 w-full overflow-hidden bg-gradient-to-br from-[#FFF4F6] to-[#FDEFF2]">
                      {variety.img ? (
                        <img
                          src={variety.img}
                          alt={variety.varietyName}
                          className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-105"
                        />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center">
                          <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-white shadow-sm">
                            <FiPackage className="h-7 w-7 text-primaryColor" />
                          </div>
                        </div>
                      )}

                      {/* Badges Overlay */}
                      <div className="absolute top-3 left-3 flex flex-wrap gap-1.5">
                        <span
                          className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold backdrop-blur-md shadow-xs ${
                            isSwadeshi
                              ? "bg-amber-100/90 text-amber-900 border border-amber-200"
                              : "bg-white/90 text-[#3D0F1B]"
                          }`}
                        >
                          <FiTag className="text-[10px] text-primaryColor" />
                          {categoryName}
                        </span>
                      </div>

                      {variety.grade && (
                        <span className="absolute top-3 right-3 rounded-full bg-[#3D0F1B]/80 px-2 py-0.5 text-[10px] font-medium text-white backdrop-blur-xs">
                          {variety.grade}
                        </span>
                      )}
                    </div>

                    {/* Content Area */}
                    <div className="p-4 sm:p-5">
                      <p className="text-xs font-medium text-gray-400">
                        {productName}
                      </p>
                      <h3 className="mt-1 text-base font-semibold text-gray-900 line-clamp-1 group-hover:text-primaryColor transition-colors">
                        {variety.varietyName}
                      </h3>

                      {variety.description && (
                        <p className="mt-1.5 text-xs text-gray-500 line-clamp-2 leading-relaxed">
                          {variety.description}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Footer / CTA Area */}
                  <div className="border-t border-gray-100 px-4 py-3 sm:px-5 bg-gray-50/50 flex items-center justify-between">
                    <span className="text-xs font-semibold text-primaryColor group-hover:underline">
                      View details
                    </span>
                    <div className="flex h-7 w-7 items-center justify-center rounded-full bg-white text-gray-400 group-hover:bg-primaryColor group-hover:text-white transition-all shadow-xs">
                      <FiArrowRight className="text-xs" />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AllSpecialVarieties;
